/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { BackendService } from './backend.service';
import { BuildingDataApiService } from '../sysque-apis/building-data-api.service';
import { CurateSearchVm, QueryParams } from '../../../..//feature/src/lib/models/curate-search-vm';
import { DocumentResult } from '@hcworkspace/sysque/toolbelt/data-access';
import { ResultInformation } from '../../../../feature/src/lib/models/result-information';
import { Discipline } from '../../../../feature/src/lib/models/result-information';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { FittingTreeView } from '@hcworkspace/sysque/toolbelt/feature';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class CurateSearchService {
  constructor(
    private backendService: BackendService,
    private buildDataApiService: BuildingDataApiService,
    private localStorageService: LocalStorageService
  ) {}

  public getCurateSearchOption(): Observable<any> {
    const groups$ = this.backendService.getCurateGroups();
    const installTypes$ = this.backendService.getInstallTypes();
    const manufacturers$ = this.backendService.getManufacturers();
    const productTypes$ = this.backendService.getProductTypes();
    const subProductTypes$ = this.backendService.getSubProductTypes();
    const materialTypes$ = this.backendService.getMaterialTypes();

    /**
     * forkJoin operator waits for all observables to
     * complete before emitting the result.
     */
    return forkJoin({
      groups: groups$,
      installTypes: installTypes$,
      manufacturers: manufacturers$,
      productTypes: productTypes$,
      subProductTypes: subProductTypes$,
      materialTypes: materialTypes$,
    }).pipe(
      map((result) => {
        return result;
      })
    );
  }

  private isValidBase64Image(base64Image: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = `data:image/png;base64,${base64Image}`;
    });
  }

  public async getFittingDataByQuery(fittingDataQuery: CurateSearchVm, unitType: string): Promise<ResultInformation[]> {
    this.localStorageService.updateStorage('resultLength', 1);
    const queries = this.createQueries(fittingDataQuery);
  
    const allQueryResults = await Promise.all(
      queries.map((query) => this.buildDataApiService.GetFittingDataByQuery(query))
    );
    const allDocs: DocumentResult[] = ([] as DocumentResult[]).concat(...allQueryResults);
    const results = await Promise.all(
      allDocs.map(async (doc) => {
        if (!doc.base64Image) return null;
        const isValid = await this.isValidBase64Image(doc.base64Image);
        if (!isValid) return null;
        return this.getResultInformation(doc, unitType);
      })
    );
    const validResults = results.filter(
      (item): item is ResultInformation => !!item
    );
    this.localStorageService.updateStorage('resultState', true);
    return validResults;
  }  

  private createQueries(fittingDataQuery: CurateSearchVm): QueryParams[] {
    const queries = [];
    if (Array.isArray(fittingDataQuery)) {
      fittingDataQuery.forEach((fittingDataQuery) => {
        const query = new QueryParams();
        query.SearchWords = fittingDataQuery.searchWords;
        query.RecordCount = 1000;
        query.ComponentGroupID = fittingDataQuery.componentGroupID;
        query.InstallTypeID = fittingDataQuery.installTypeID;
        query.ManufacturerID = fittingDataQuery.manufacturerID;
        query.MaterialID = fittingDataQuery.materialID;
        query.Discontinued = false;
        queries.push(query);
      });
    } else {
      const query = new QueryParams();
      query.SearchWords = fittingDataQuery.searchWords;
      query.RecordCount = 1000;
      query.ComponentGroupID = fittingDataQuery.componentGroupID;
      query.InstallTypeID = fittingDataQuery.installTypeID;
      query.ManufacturerID = fittingDataQuery.manufacturerID;
      query.MaterialID = fittingDataQuery.materialID;
      query.Discontinued = false;
      queries.push(query);
    }
    return queries;
  }

  private validateTypeOfProject(fittingData: DocumentResult, unitType: string): boolean {
    if (fittingData.fittingUnitsName.toLocaleLowerCase() === unitType.toLocaleLowerCase()) {
      return true;
    }
    return false;
  }

  public async getResultInformation(fittingData: DocumentResult, unitType: string): Promise<ResultInformation | null> {
    const discipline = this.getDiscipline(
      fittingData.installType,
      fittingData.componentGroup,
      fittingData.productDisciplineID
    );

    if (!this.isValidDisciplineAndProject(discipline, fittingData, unitType)) {
      return null;
    }

    const resultInfo = this.createResultInformation(fittingData, discipline);

    try {
      const sizes = await this.buildDataApiService.getSizeData(fittingData.fittingEntryID);
      resultInfo.sizes = this.getDataSource(sizes);
     
    } catch (error) {
      console.error('Error fetching size data:', error);
      return null;
    }

    return resultInfo;
  }

  private isValidDisciplineAndProject(discipline: Discipline, fittingData: DocumentResult, unitType: string): boolean {
    return (
      [Discipline.Electrical, Discipline.Hangers, Discipline.CableTray].includes(discipline) &&
      this.validateTypeOfProject(fittingData, unitType)
    );
  }

  private createResultInformation(fittingData: DocumentResult, discipline: Discipline): ResultInformation {
    const resultInfo = new ResultInformation();
    const path = fittingData.catalogLocation.substring(1);

    resultInfo.ManufacturerResult = fittingData.manufacturerName;
    resultInfo.NameResult = fittingData.sysQueLongDescription;
    resultInfo.Path = path;
    resultInfo.ImagePath = `data:image/png;base64,${fittingData.base64Image}`;
    resultInfo.Image = fittingData.base64Image;
    resultInfo.Discipline = discipline;
    resultInfo.FittingId = fittingData.fittingEntryID;
    resultInfo.FittingIdNumber = fittingData.fittingIDNumber;

    return resultInfo;
  }

  public getDataSource(sizes: string[]): { text: string; items?: { text: string }[] }[] {
    if (sizes[0] === '' || sizes.length === 0) {
      return [
        {
          text: 'Add to System',
        },
      ];
    }
    const items: any[] = [];
    sizes.forEach((i) => {
      items.push({ text: i });
    });
    return [
      {
        text: 'Add to System',
      },
      {
        text: 'Sizes',
        items: items,
      },
    ];
  }

  public getDiscipline(installType: string, componentGroup: string, productDisciplineId: number): Discipline {
    if (installType === 'Hanger' || componentGroup === 'Hangers') {
      return Discipline.Hangers;
    }

    /**
     * This uses a map for the productDisciplineId to Discipline mapping,
     * making it more readable and maintaining the same logic as switch statement.
     */
    const disciplineMap: Map<number, Discipline> = new Map([
      [2, Discipline.PlumbingPiping],
      [4, Discipline.Electrical],
      [5, Discipline.SheetMetal],
      [6, Discipline.Mep],
      [8, Discipline.Discontinued],
      [9, Discipline.CableTray],
    ]);

    return disciplineMap.get(productDisciplineId) || Discipline.None;
  }

  public getFittingSizes(documents: ResultInformation[]): Promise<ResultInformation[]> {
    documents.forEach((doc) => {
      this.buildDataApiService.getSizeData(doc.FittingId).then((data) => {
        doc.sizes = data;
      });
    });

    return Promise.all(documents);
  }

  public getResultInformationFromTreeViewFolderItems(treeViewItems: FittingTreeView[]): Promise<ResultInformation[]> {
    let items: any[] = [];

    for (let index = 0; index < treeViewItems?.length; index++) {
      const item = treeViewItems[index];
      let itemResultInformation = this.convertTreeViewItemToResultInformation(item);
      items.push(itemResultInformation);
    }
    return Promise.all(items);
  }

  public async convertTreeViewItemToResultInformation(
    treeViewItem: FittingTreeView
  ): Promise<ResultInformation | null> {
    let resultInformation = new ResultInformation();
    resultInformation.ManufacturerResult = treeViewItem.manufacturerName;
    resultInformation.NameResult = treeViewItem.text;
    resultInformation.ImagePath = 'data:image/png;base64,' + treeViewItem.image;
    resultInformation.Image = treeViewItem.image;
    resultInformation.Discipline = Discipline.None;
    resultInformation.FittingId = treeViewItem.fittingEntryID;
    resultInformation.FittingIdNumber = treeViewItem.fittingIDNumber;
    resultInformation.Path = this.getPathWithoutFile(treeViewItem.id);
    const sizes =
      treeViewItem.fittingEntryID == 0 ? [] : await this.buildDataApiService.getSizeData(treeViewItem.fittingEntryID);
    resultInformation.sizes = this.getDataSource(sizes);
    return resultInformation;
  }

  private getPathWithoutFile(path: string): string {
    let parts = path.split('\\');
    parts.pop();
    let newPath = parts.join('\\');
    return newPath;
  }
}
